import * as React from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import type { IGraphClient } from '../../../src/services/GraphService';
import type { IWorkspace } from '../../api/src/workspace';
import type { IQueueItem } from '../../api/src/queue';
import type { IWorkspaceSummary } from '../api';
import type { GraphAuditLog } from '../GraphAuditLog';
import type { Route } from './router';
import type { IInboxState } from '../filing/useInbox';
import type { FirmRecords } from '../records/FirmRecords';

export interface IToast {
  text: string;
  action?: { label: string; run: () => void };
}

export interface IAppContext {
  account: AccountInfo;
  /** Delegated Graph: read, and write files (Files.ReadWrite.All). */
  graph: IGraphClient;
  /** Delegated Graph with Sites.Manage.All, asked for the first time something is changed (audit list). */
  manageGraph: IGraphClient;
  workspaces: IWorkspaceSummary[];
  workspace: IWorkspace;
  /** Saves settings (checked by the backend) and keeps the saved copy. */
  saveWorkspace: (next: IWorkspace) => Promise<IWorkspace>;
  switchLibrary: (driveId: string) => void;
  audit: GraphAuditLog;
  /** The firm's records (clients, applications, checklists, events), in lists in its own site. */
  records: FirmRecords;
  queue: IQueueItem[];
  /** Fetches the queue again, and brings the Filing Review list in SharePoint in step with it. */
  reloadQueue: () => Promise<void>;
  credits?: number;
  setCredits: (n: number) => void;
  /** 'trial' or 'paid' (see the credits endpoint for what the trial allows). */
  plan: 'trial' | 'paid';
  /** Free trial: what's left this trial year. */
  trial?: import('../api').ITrialAllowance;
  refreshPlan: () => void;
  route: Route;
  go: (r: Route) => void;
  toast: (t: IToast) => void;
  inbox: IInboxState;
}

export const AppContext = React.createContext<IAppContext | undefined>(undefined);

export function useApp(): IAppContext {
  const ctx: IAppContext | undefined = React.useContext(AppContext);
  if (!ctx) {
    throw new Error('useApp outside the app');
  }
  return ctx;
}

/** A plain sentence for any error, for people. */
export function errorText(error: unknown): string {
  const e = error as { statusCode?: number; message?: string; code?: string; errorCode?: string };
  // Microsoft sign-in problems, in plain words.
  if (e?.errorCode === 'user_cancelled') {
    return 'Microsoft sign-in was cancelled. Nothing changed; try again when you\'re ready.';
  }
  if (e?.errorCode === 'timed_out' || e?.errorCode === 'monitor_window_timeout') {
    return 'Microsoft took too long to answer. Try again in a moment.';
  }
  if (e?.statusCode === 403) {
    return 'You don\'t have permission to do that in this library. Ask whoever manages the site.';
  }
  if (e?.statusCode === 404) {
    return 'That item or folder no longer exists. It may have been moved or deleted.';
  }
  if (e?.statusCode === 429 || e?.statusCode === 503) {
    return 'Microsoft 365 is busy right now. Wait a minute and try again.';
  }
  return e?.message || 'Something went wrong. Try again in a moment.';
}
